import React, { FunctionComponent, ReactElement, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { NavigationContext } from '@context/NavigationContext'
import { PredictionContext } from '@context/PredictionContext'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { Choice, OneOfManyQuestion } from '@models/Survey'
import { PredictionData } from '@models/Prediction/Prediction'
import { AnswerType } from '@models/Survey/Answer'
import { Pages } from '@models/Pages'
import { TabbedPane } from '@components/Survey/TabbedPane/TabbedPane'
import './RectifyPrediction.scss'
import { SurveyContext } from '@context/SurveyContext'
import {
  categoryHasProbabilityForecasting,
  categoryUsesDsmpPrediction,
  getCategory,
  hasNextSurveyInCategory,
} from '@utils/categoryFlow'

export interface OwnProps {}

type Props = OwnProps

const RectifyPrediction: FunctionComponent<Props> = (): ReactElement => {
  const { t, i18n } = useTranslation('RectifyPrediction')
  const { setPrevHandler, setNextHandler } = useContext(NavigationContext)
  const { survey, setNextStep, continueToSecondQuestionnaire } = useContext(SurveyContext)
  const { results, prediction, setRectifiedPrediction, rectifiedPrediction, submitRectification } =
    useContext(PredictionContext)

  const category = getCategory(survey)
  const isDsmp = categoryUsesDsmpPrediction(category)
  const hasProbabilityForecasting = categoryHasProbabilityForecasting(category)

  const [currentFilteredData, setCurrentFilteredData] = useState<PredictionData[]>([])
  const [currentQuestion, setCurrentQuestion] = useState<OneOfManyQuestion>()

  const navigate = useNavigate()
  const predictionUrl = `/${Pages.Survey}/${SurveyStep.Prediction}`
  const probabilityUrl = `/${Pages.Survey}/${SurveyStep.Probability}`

  const navigateToPrediction = () => navigate(predictionUrl)
  const navigateToProbability = () => navigate(probabilityUrl)

  useEffect(() => {
    if (isDsmp) {
      setNextStep(SurveyStep.Results)
      return
    }

    if (!prediction) {
      navigateToPrediction()
    }

    setPrevHandler(() => {
      setRectifiedPrediction(undefined)
      setNextStep(SurveyStep.Prediction)
    })
  }, [isDsmp, prediction])

  const nextHandler = () => {
    if (rectifiedPrediction) {
      submitRectification(rectifiedPrediction)
      setRectifiedPrediction(undefined)
    }
    if (hasProbabilityForecasting) {
      navigateToProbability()
      return
    }
    if (hasNextSurveyInCategory(survey)) {
      continueToSecondQuestionnaire().then(() => setNextStep(SurveyStep.Answer))
      return
    }
    setNextStep(SurveyStep.Results)
  }

  useEffect(() => {
    if (isDsmp) {
      return
    }
    if (rectifiedPrediction) {
      setNextHandler(nextHandler)
    } else {
      setNextHandler(undefined)
    }
  }, [rectifiedPrediction, isDsmp])

  const currentLocale = (i18n.language ?? 'fr').substring(0, 2)
  useEffect(() => {
    if (isDsmp) {
      return
    }
    const filteredData = results?.prediction?.data?.filter(({ id }) => id !== prediction?.id) ?? []

    filteredData.push({
      id: 665,
      value: 665,
      color: '',
      names: {
        acr: {
          en: 'another',
          fr: 'another',
        },
        full: {
          en: t('choices.another'),
          fr: t('choices.another'),
        },
        short: {
          en: t('choices.another'),
          fr: t('choices.another'),
        },
      },
    })

    filteredData.push({
      id: 666,
      value: 666,
      color: '',
      names: {
        acr: {
          en: 'no_vote',
          fr: 'no_vote',
        },
        full: {
          en: t('choices.no_vote'),
          fr: t('choices.no_vote'),
        },
        short: {
          en: t('choices.no_vote'),
          fr: t('choices.no_vote'),
        },
      },
    })

    const choices = filteredData.map(({ id, names: { full } }: PredictionData) => {
      return {
        id: `${id}`,
        text: full[currentLocale],
      } as Choice
    })

    const question = {
      choices,
    } as OneOfManyQuestion

    setCurrentFilteredData(filteredData)
    setCurrentQuestion(question)
  }, [results, isDsmp])

  const handleSelection = (targetIdStr: AnswerType): void => {
    const newTarget = currentFilteredData.find(({ id }) => id === parseInt(targetIdStr as string))
    setRectifiedPrediction(newTarget)
  }

  const tabs = [
    {
      text: t('navigation.title'),
    },
  ]

  if (isDsmp) {
    return null
  }

  return (
    <TabbedPane
      tabs={tabs}
      contentText={t('questionText')}
      choices={currentQuestion}
      onChoiceSelection={handleSelection}
      propClassName="RectifyPrediction"
    />
  )
}

export default RectifyPrediction
